import { Suspense, useEffect, useRef, useState } from "react";
import { Loading } from "./Loading";
import { DashBoardComponentProps } from "../data/data";
import { MeasurementCard } from "../../../lovable/components/MeasurementCard";
import { getBodyFatTable } from "../data/bodyfatTable";
import { ObjectModel } from "../../../common/types";
import ThreeDModel from "../../../components/3DModel";
import { toRanges } from "./TrendsTab";
import { Canvas } from "@react-three/fiber";

export const OverallFitness: React.FC<DashBoardComponentProps> = ({
  clientHolder,
  clientData,
  objectModels,
  measurementDatas,
}) => {
  const delayTimeout = useRef<NodeJS.Timeout | null>(null);

  // 1) Initialize myTarget from clientData.target.body_fat, or 0 if none:
 
  const lastIndex = objectModels.length - 1;

  // 2) Keep bodyFat as an array; initially empty:
  const bodyFat:number[] = measurementDatas
  .filter((m) => m.measurements.body_fat !== undefined)
  .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  .map((m) => m.measurements.body_fat!) as number[];
  const [loading, setLoading] = useState(false);
  const currentBodyFat = () => {
    if (bodyFat.length === 0) return 0;
    return Math.round(bodyFat[bodyFat.length - 1]);
  };
  const currentTarget =  clientData.target?.measurements.body_fat 
  const initialTarget = currentTarget ?? currentBodyFat();
  const [myTarget, setMyTarget] = useState<number>(
   initialTarget
  );

  const [activeModel, setActiveModel] = useState<number>(0);

  const [models, setModels] = useState<ObjectModel[]>(()=>{return objectModels.length>0?[{...objectModels[lastIndex],...{id:String(currentBodyFat())} }]:[]});


  // We can compute ranges once—clientData.gender and clientData.age are assumed stable:
  const ranges = toRanges(getBodyFatTable(clientData.gender, clientData.age));

  // Whenever `myTarget` changes, run onTargetChange(true)
  useEffect(() => {
    if (myTarget > 0 && myTarget <= 100) {
        console.log("Setting target to:", myTarget);
      onTargetChange(myTarget, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myTarget]);

  // 3) Whenever measurementDatas changes, recalc the bodyFat array:
 


  const setBFTarget = async (percentage: number) => {
    clientHolder.setTarget(percentage);
    // small delay to let the setter propagate:
    setTimeout(async () => {
      await onTargetChange(percentage, true);
    }, 200);
  };

  const onTargetChange = async (percentage: number, commit: boolean) => {
    if (percentage < 1 || percentage > 100) {
      return;
    }

    const existingIndex = models.findIndex(
      (m) => m.id === percentage.toString()
    );
    if (existingIndex !== -1) {

      setActiveModel(existingIndex);
    
      
      setLoading(false);
      return;
    }

    if (commit && !loading) {

      await fetchEstimation(percentage);
      return;
    }

    if (delayTimeout.current) {
      clearTimeout(delayTimeout.current);
    }

  };

  const fetchEstimation = async (percentage: number) => {
    setLoading(true);
    const estimation = (await clientHolder.getEstimation(
      percentage / 100.0
    )) as string;

    const newModel: ObjectModel = {
      obj: estimation,
      id: percentage.toString(),
      createdAt: new Date().toISOString(),
      visibleEffects: [],
      showMeasurement: "",
      measurements: {
        height: 0,
        head: 0,
        neck: 0,
        chest: 0,
        waist: 0,
        buttocks: 0,
        leg_inner: 0,
        arm_outer: 0,
        leg_circ: 0,
      },
    };
    const newModelList = [...models, newModel];
    setModels(newModelList);
    setActiveModel(newModelList.length - 1);

    setLoading(false);
  };

  const showResults = () => models.length > 0;



  // If any of these props are missing, show a loading spinner:
  if (!clientHolder || !clientData || !measurementDatas) {
    console.warn("Client holder or client data is not available");
    return <Loading />;
  }

  // If we haven’t yet received any body-fat readings, show a placeholder:
  if (bodyFat.length === 0) {
    return <div>Loading...</div>;
  }

  // Render as soon as we have clientData and a non-zero myTarget
  return clientData ? (
    <>
      <MeasurementCard
        unit="%"
        title="Body Fat"
        value={currentBodyFat()}
        ranges={ranges}
        isTarget={true}
        myTarget={myTarget}
        onTargetChange={onTargetChange}
        setTarget={setBFTarget}
      />
      {showResults() && (
        <div className="resultPhoto m-0">
        <Canvas
          camera={{
            fov: 40,
            aspect: window.innerWidth / window.innerHeight,
            zoom: 1.3,
            near: 1,
            far: 1100,
          }}
        >
          <color attach="background" args={["white"]} />
          <Suspense fallback={null}>
          <ThreeDModel
          models={{models: models, activeModel: activeModel}}
          activeModel={activeModel}
          showMeasurement={""}
      />
          </Suspense>
        </Canvas>
      </div>
        )
         

      }
    </>
  ) : (
    <Loading />
  );
};
